import React, { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';

const MissionSection = () => {
  const [wordOpacities, setWordOpacities] = useState([]);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const sectionRef = useRef(null);
  const textRef = useRef(null);
  const wordsRef = useRef([]);
  const videoRef = useRef(null);

  // Split text into words
  const text = "Built to empower, stabilise, and accelerate, Gazeal Advance gives estate agents control over when they get paid. By combining Reservation agreements with AgentsCapital's proven model, it removes uncertainty, improves cash flow, and helps agents operate with confidence.";
  const secondText = "Below, Sean Barrett shares how this partnership is redefining how agents get paid.";

  const words = text.split(' ');
  const secondWords = secondText.split(' ');

  useEffect(() => {
    const handleScroll = () => {
      if (!textRef.current || wordsRef.current.length === 0) return;

      const windowHeight = window.innerHeight;
      const scrollY = window.scrollY;
      
      // Calculate opacity for each word based on its position
      const newOpacities = wordsRef.current.map((wordEl) => {
        if (!wordEl) return 0.2;
        
        const rect = wordEl.getBoundingClientRect();
        const wordTop = rect.top + scrollY;
        const wordCenter = wordTop + rect.height / 2;
        
        // Distance from viewport center
        const viewportCenter = scrollY + windowHeight / 2;
        const distance = Math.abs(wordCenter - viewportCenter);
        
        // Calculate opacity based on distance (closer = brighter)
        const maxDistance = windowHeight / 2;
        let opacity = 1 - (distance / maxDistance);
        
        // Clamp between 0.2 and 1
        opacity = Math.max(0.2, Math.min(1, opacity));
        
        return opacity;
      });
      
      setWordOpacities(newOpacities);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
    // Trigger video play via postMessage to Vimeo player
    if (videoRef.current) {
      const iframe = videoRef.current;
      iframe.contentWindow.postMessage('{"method":"play"}', '*');
    }
  };

  return (
    <section id="sean-barrett-video" ref={sectionRef} className="relative py-20 md:py-32 bg-black">
      <div className="max-w-[900px] mx-auto px-6 lg:px-12">
        {/* Text Content with Word-by-Word Scroll Effect */}
        <div 
          ref={textRef}
          className="text-center mb-8 md:mb-12"
        >
          <p className="text-2xl md:text-3xl lg:text-4xl leading-relaxed text-white mb-0">
            {words.map((word, index) => {
              // Bold certain words - remove punctuation for comparison
              const cleanWord = word.replace(/[,.]/, '');
              const isBold = ['Gazeal', 'Advance', "AgentsCapital's"].includes(cleanWord);
              
              return (
                <span
                  key={index}
                  ref={(el) => wordsRef.current[index] = el}
                  className={`inline-block transition-opacity duration-300 ${isBold ? 'font-medium' : 'font-light'}`}
                  style={{ 
                    opacity: wordOpacities[index] || 0.2,
                    marginRight: word.endsWith(',') || word.endsWith('.') ? '0' : '0.3em'
                  }}
                >
                  {word}
                </span>
              );
            })}
          </p>
        </div>

        <div className="text-center mb-16 md:mb-24">
          <p className="text-lg md:text-xl text-white/80 leading-relaxed">
            Below, <span className="text-[#59d862] font-medium">Sean Barrett, Agents Capital Founder</span> shares how this partnership is redefining how agents get paid.
          </p>
        </div>

        {/* Video with Green Glow and Interactive Play Button */}
        <div className="relative max-w-4xl mx-auto">
          {/* Glow Effect Behind Video */}
          <div className="absolute -inset-8 bg-gradient-to-r from-[#59d862]/20 via-[#59d862]/30 to-[#59d862]/20 rounded-3xl blur-3xl opacity-60 animate-pulse" />
          <div className="absolute -inset-4 bg-[#59d862]/10 rounded-2xl blur-2xl" />
          
          {/* Video Container */}
          <div className="relative aspect-video bg-black border border-[#59d862]/30 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(89,216,98,0.2)] group">
            {/* Vimeo Video Embed */}
            <iframe 
              ref={videoRef}
              src={`https://player.vimeo.com/video/1167347461?badge=0&autopause=0&player_id=0&app_id=58479&loop=1${isVideoPlaying ? '&autoplay=1' : ''}`}
              frameBorder="0" 
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
              referrerPolicy="strict-origin-when-cross-origin"
              className="absolute top-0 left-0 w-full h-full"
              title="Sean Barrett - Gazeal Advance Partnership"
            />

            {/* Frosty Translucent Overlay with Play Button */}
            {!isVideoPlaying && (
              <div 
                className="absolute inset-0 bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm cursor-pointer transition-all duration-500 hover:backdrop-blur-md z-10"
                onClick={handlePlayVideo}
              >
                {/* Frosted glass effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
                
                {/* Glossy Play Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative group/play">
                    {/* Glow behind button */}
                    <div className="absolute inset-0 bg-[#59d862] rounded-full blur-2xl opacity-0 group-hover:opacity-40 transition-opacity duration-300" />
                    
                    {/* Main button */}
                    <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-[#59d862] to-[#4bc252] shadow-[0_8px_32px_rgba(89,216,98,0.4)] 
                                  flex items-center justify-center
                                  transform transition-all duration-300 
                                  group-hover/play:scale-110 group-hover/play:shadow-[0_12px_48px_rgba(89,216,98,0.6)]
                                  active:scale-95">
                      {/* Glossy shine effect */}
                      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/40 via-white/10 to-transparent opacity-80" />
                      
                      {/* Inner glow */}
                      <div className="absolute inset-2 rounded-full bg-gradient-to-br from-white/20 to-transparent" />
                      
                      {/* Play icon */}
                      <Play className="w-8 h-8 md:w-10 md:h-10 text-black relative z-10 ml-1" fill="black" />
                      
                      {/* Pulse ring */}
                      <div className="absolute inset-0 rounded-full border-2 border-[#59d862] animate-ping opacity-20" />
                    </div>
                  </div>
                </div>

                {/* Call to action text */}
                <div className="absolute bottom-8 left-0 right-0 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-white text-sm md:text-base font-medium drop-shadow-lg">
                    Click to watch Sean Barrett's story
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Corner Accents */}
          <div className="absolute -top-3 -left-3 w-16 h-16 border-l-2 border-t-2 border-[#59d862]/40 rounded-tl-2xl" />
          <div className="absolute -bottom-3 -right-3 w-16 h-16 border-r-2 border-b-2 border-[#59d862]/40 rounded-br-2xl" />
        </div>
      </div>
    </section>
  );
};

export default MissionSection;
